<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <!-- Loading state -->
    <empty-state v-if="isLoading" :title="$t('loading')" :loading="true" />

    <!-- Empty state: No restaurants selected -->
    <empty-state
      v-else-if="!selectedRestaurants.length"
      icon="mdi-store"
      :title="$t('no_restaurants_selected')"
      :subtitle="$t('select_restaurants_to_view_report')"
    />

    <!-- Empty state: No data -->
    <empty-state
      v-else-if="!restaurantCards.length"
      icon="mdi-food-off"
      :title="$t('no_data')"
      :subtitle="$t('no_meals_for_selected_period')"
    />

    <!-- Restaurant cards -->
    <v-card
      v-else
      v-for="restaurant in restaurantCards"
      :key="restaurant.id"
      flat
      class="company-card mt-6"
      outlined
    >
      <v-card-title v-if="!device.isMobile">
        <v-row align="center" no-gutters>
          <v-col>
            <div class="text-center">
              <!-- Restaurant Name -->
              <div class="company-header mb-4">
                <h2 class="company-title">
                  {{ toLatin(restaurant.name) }}
                </h2>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider v-if="!device.isMobile"></v-divider>
      <v-card-text class="pa-0">
        <!-- Desktop View -->
        <v-data-table
          v-if="!device.isMobile"
          :headers="headers"
          :items="customSearch(restaurant.meals, headers, search)"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
        >
          <template v-slot:item.name="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ item.name }}
                </span>
              </template>
              <span>{{ item.id }}</span>
            </v-tooltip>
          </template>
          <template #body.append>
            <tr class="font-weight-bold totals-row">
              <td v-if="!device.isMobile"></td>
              <td>{{ $t("total") }}</td>
              <td v-for="shift in shiftsWithData" :key="shift" class="text-center">
                {{ formatShiftQuantity(restaurant.totals[getShiftKey(shift)]) }}
              </td>
              <td class="text-center">{{ restaurant.totals.total }}</td>
            </tr>
          </template>
          <!-- Dynamic shift templates -->
          <template
            v-for="shift in shiftsWithData"
            v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
          >
            <span :key="shift">
              <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
                {{ item[getShiftKey(shift)].customerQuantity }}
              </span>
              <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
                <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on"
                      >{{ item[getShiftKey(shift)].restaurantQuantity }}R</span
                    >
                  </template>
                  <span>{{ $t("ordered_by_restaurant") }}</span>
                </v-tooltip>
              </span>
              <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
            </span>
          </template>
        </v-data-table>

        <!-- Mobile View -->
        <mobile-data-cards
          v-else
          :title="toLatin(restaurant.name)"
          :headers="mobileHeaders"
          :items="customSearch(restaurant.meals, headers, search)"
          item-key="id"
          :fields-per-row="null"
          :bold-fields="['name']"
          :show-row-number="false"
          class="px-3"
        >
          <template
            v-for="shift in shiftsWithData"
            v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
          >
            <span :key="shift">
              <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
                {{ item[getShiftKey(shift)].customerQuantity }}
              </span>
              <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
                <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
                {{ item[getShiftKey(shift)].restaurantQuantity }}R
              </span>
              <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
            </span>
          </template>
        </mobile-data-cards>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import EmptyState from "@/components/global/EmptyState.vue";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import { SHIFTS, getShiftKey } from "@/constants/shifts";

export default {
  name: "CompanyMultiRestaurantTabPackages",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      packages: null,
      isLoading: false,
    };
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    selectedShifts() {
      return this.$store.state.report.filters.selectedShifts;
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    restaurantCards() {
      if (!this.packages || !Array.isArray(this.packages) || this.packages.length === 0) {
        return [];
      }

      // Map each restaurant's data
      return this.packages.map((restaurantData) => {
        const restaurant = this.restaurants.find((r) => r.id === restaurantData.id);
        return {
          id: restaurantData.id,
          name: restaurant?.name || restaurantData.name,
          meals: restaurantData.meals || [],
          totals: restaurantData.totals || {
            shift_one: { totalQuantity: 0 },
            shift_two: { totalQuantity: 0 },
            shift_three: { totalQuantity: 0 },
            shift_four: { totalQuantity: 0 },
            total: 0,
          },
        };
      });
    },
    shiftsWithData() {
      // selectedShifts is an array from store [1, 2, 3] or empty []
      if (Array.isArray(this.selectedShifts) && this.selectedShifts.length > 0) {
        return this.selectedShifts.sort((a, b) => a - b);
      }
      // If no shifts selected, show all
      return [1, 2, 3, 4];
    },
    filteredShifts() {
      return SHIFTS.filter((shift) => this.shiftsWithData.includes(shift.id));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
        ...this.filteredShifts.map((s) => ({
          text: this.$t(s.label),
          value: s.key,
          align: "center",
        })),
        { text: this.$t("total"), value: "total", align: "center" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("meal"), value: "name", cols: 12 },
        ...this.filteredShifts.map((s) => ({
          text: this.$t(s.label),
          value: s.key,
          cols: 3,
        })),
        { text: "Σ", value: "total", cols: 3 },
      ];
    },
  },
  watch: {
    dates: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    selectedRestaurants: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      const sheets = this.restaurantCards.map((restaurant) => ({
        name: this.toLatin(restaurant.name),
        headers: this.headers,
        items: this.formatMealsForExport(restaurant.meals),
        title: [this.$t("report"), this.$t("meals")].join(" "),
        subtitle: `${this.subtitle} - ${this.toLatin(restaurant.name)}`,
      }));
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("meals")].join(" ") + ".xlsx",
        sheets,
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const tables = this.restaurantCards.map((restaurant) => ({
        headers: this.headers,
        items: this.formatMealsForExport(restaurant.meals),
        title: this.toLatin(restaurant.name),
      }));
      const restaurantNames = this.restaurantCards
        .map((r) => this.toLatin(r.name))
        .join(", ");

      const restaurantsFooter = `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
        <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
      </div>`;

      this.printDocument({
        tables,
        title: [this.$t("report"), this.$t("meals")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: restaurantsFooter,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    getShiftKey,
    formatShiftQuantity(shiftData) {
      if (!shiftData || shiftData.totalQuantity === 0) return "0";
      const { customerQuantity = 0, restaurantQuantity = 0 } = shiftData;
      const parts = [];
      if (customerQuantity > 0) parts.push(customerQuantity);
      if (restaurantQuantity > 0) parts.push(`${restaurantQuantity}R`);
      return parts.join(" + ") || "0";
    },
    formatMealsForExport(meals) {
      return meals.map((item, index) => {
        const exportItem = { num: index + 1, name: item.name };
        this.shiftsWithData.forEach((shift) => {
          exportItem[this.getShiftKey(shift)] = this.formatShiftQuantity(
            item[this.getShiftKey(shift)]
          );
        });
        exportItem.total = item.total;
        return exportItem;
      });
    },
    async fetchData() {
      if (!this.companyId || this.selectedRestaurants.length === 0) {
        this.packages = [];
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dates.from,
          date2: this.dates.to,
          company: this.companyId,
        };

        // Fetch data for each selected restaurant
        const promises = this.selectedRestaurants.map((restaurantId) =>
          reportService.getPackagesForMoreRestaurant(restaurantId, params)
        );

        const responses = await Promise.all(promises);

        // Combine all responses and add restaurant IDs
        this.packages = responses
          .map((response, index) => {
            if (Array.isArray(response) && response.length > 0) {
              return {
                ...response[0],
                id: this.selectedRestaurants[index],
              };
            }
            return null;
          })
          .filter(Boolean);
      } catch (error) {
        console.error("Error fetching packages:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.company-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, rgba(0, 0, 0, 0.02) 100%);
}

.company-card:hover {
  border-color: rgba(25, 118, 210, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.company-header {
  position: relative;
}

.company-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -0.5px;
}

@media (max-width: 768px) {
  .company-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 600px) {
  .company-card {
    border-radius: 8px;
  }

  .company-title {
    font-size: 1.3rem;
  }
}
</style>
